// Voucher redemption history tab for Customer 360.

import { useMemo } from 'react';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ReportTable } from '@/features/reports';
import type { CustomerVoucherRedemptionRow } from '../types';
import { formatDateTime } from '../format';

type Row = CustomerVoucherRedemptionRow;

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    {
      key: 'redeemed_at',
      header: 'Date',
      render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">{formatDateTime(row.redeemed_at)}</span>,
    },
    {
      key: 'voucher_code',
      header: 'Voucher',
      render: (_v, row) => (
        <span className="text-sm font-mono font-semibold text-violet-700 dark:text-violet-300">{row.voucher_code}</span>
      ),
    },
    {
      key: 'bill_number',
      header: 'Bill',
      render: (_v, row) => (
        <div>
          <p className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">{row.bill_number || '—'}</p>
          {row.location_name ? <p className="text-xs text-neutral-500 dark:text-neutral-400">{row.location_name}</p> : null}
        </div>
      ),
    },
    {
      key: 'order_total',
      header: 'Bill Total',
      numeric: true,
      render: (_v, row) => <span className="text-sm text-neutral-900 dark:text-neutral-100 tabular-nums">{formatPaise(row.order_total, currency)}</span>,
    },
    {
      key: 'discount_amount',
      header: 'Discount',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm font-bold text-success-600 dark:text-success-300 tabular-nums">−{formatPaise(row.discount_amount, currency)}</span>
      ),
    },
  ];
}

export interface VoucherRedemptionTableProps {
  rows: Row[];
  total?: number;
  loading?: boolean;
}

export function VoucherRedemptionTable({ rows, total, loading }: VoucherRedemptionTableProps) {
  const currency = useTenantCurrency();
  const columns = useMemo(() => makeColumns(currency), [currency]);
  return (
    <ReportTable<Row>
      title="Voucher History"
      subtitle="Vouchers and coupons this customer has redeemed."
      columns={columns}
      data={rows}
      loading={loading}
      totalCount={total ?? rows.length}
      emptyTitle="No voucher activity"
      emptyDescription="Vouchers redeemed by this customer will appear here."
    />
  );
}
